import { Header } from "../components/promill/Header";
import { Footer } from "../components/promill/Footer";
import { Newsletter } from "../components/promill/Newsletter";
import { Link } from "react-router-dom";
import {
    Wheat, Soup, Droplet, Sprout, ArrowRight,
    ShieldCheck, HeartHandshake, Sparkles, Check, X
} from "lucide-react";
import { LOGO_MARK, PILLAR_IMAGES, HERO_IMAGE } from "../data/promill";

const PROMISE = [
    { icon: Wheat, title: "Millet Wraps", desc: "Soft, wholesome and satisfying.", image: PILLAR_IMAGES.wrap, accent: "bg-pm-yellow/20" },
    { icon: Soup, title: "Protein Bowls", desc: "Balanced, hearty and delicious.", image: PILLAR_IMAGES.bowl, accent: "bg-pm-greenBright/15" },
    { icon: Droplet, title: "Clean Sauces", desc: "Crafted in-house with clean ingredients.", image: PILLAR_IMAGES.sauce, accent: "bg-pm-orange/15" },
    { icon: Sprout, title: "Fresh Add-ons", desc: "Crisp veggies and superfood toppings.", image: PILLAR_IMAGES.addons, accent: "bg-pm-yellow/20" },
];

const RULES = [
    { icon: ShieldCheck, title: "No nasties, ever.", desc: "Zero refined sugar, zero seed oils, zero artificial preservatives. Every label is clean enough to read in 10 seconds." },
    { icon: HeartHandshake, title: "Farmer-first sourcing.", desc: "Stone-milled millets sourced directly from farms across Karnataka, Andhra Pradesh and Rajasthan." },
    { icon: Sparkles, title: "Macro-honest meals.", desc: "Every meal hits at least 22g of clean protein, stays under 500 kcal, and is engineered for real Indian bodies in motion." },
];

const WHAT_IN = ["Stone-milled Indian millets", "Cold-pressed oils", "Real fruit, real spice", "Hand-crafted sauces", "Compostable packaging"];
const WHAT_OUT = ["Refined sugar", "Seed oils & trans fats", "Artificial preservatives", "Mystery flavorings", "Single-use plastics"];

const FoodPromisePage = () => (
    <div data-testid="food-promise-page" className="bg-pm-cream min-h-screen">
        <Header />

        {/* HERO — full-bleed image stripe + overlay copy */}
        <section className="relative bg-pm-cream overflow-hidden border-b border-pm-black/5">
            <div className="relative max-w-7xl mx-auto grid lg:grid-cols-12 items-center min-h-[480px] md:min-h-[560px]">
                <div className="lg:col-span-6 px-4 sm:px-6 lg:px-8 py-10 lg:py-0 relative z-10">
                    <div className="inline-flex items-center gap-2 mb-4">
                        <img src={LOGO_MARK} alt="" className="h-6 w-6" />
                        <span className="text-pm-green font-bold tracking-[0.22em] uppercase text-[11px] md:text-xs">
                            The ProMill Promise
                        </span>
                    </div>
                    <h1
                        data-testid="food-promise-headline"
                        className="text-4xl sm:text-5xl md:text-6xl lg:text-[4.5rem] font-extrabold tracking-[-0.02em] text-pm-black leading-[1.02]"
                    >
                        Fast food, <br />
                        <span className="text-pm-yellow">rebuilt cleaner.</span>
                    </h1>
                    <p className="mt-5 text-base md:text-lg text-pm-black/70 leading-relaxed max-w-lg">
                        Protein-first meals, millet-powered bases, cleaner sauces, and ingredients
                        you can feel good about — every single time.
                    </p>
                    <div className="mt-7 flex flex-wrap gap-3">
                        <Link to="/menu" data-testid="fp-explore-menu" className="btn-brutal btn-brutal--accent">
                            Explore Menu
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                        <Link to="/contact" className="btn-brutal btn-brutal--ghost">
                            Join Waitlist
                        </Link>
                    </div>
                </div>
                <div className="lg:col-span-6 relative w-full">
                    <img
                        src={HERO_IMAGE}
                        alt="ProMill food spread"
                        className="w-full h-auto object-contain"
                    />
                </div>
            </div>
        </section>

        {/* PROMISE CARDS */}
        <section className="relative py-16 md:py-24 bg-pm-cream">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center max-w-2xl mx-auto mb-12">
                    <span className="inline-block text-pm-orange font-bold tracking-[0.25em] uppercase text-[11px] mb-3">
                        Four Pillars
                    </span>
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-[-0.02em] text-pm-black leading-tight">
                        Everything we serve, <br className="hidden sm:block" />
                        <span className="text-pm-green">stands on these four.</span>
                    </h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                    {PROMISE.map((p, idx) => (
                        <div
                            key={p.title}
                            data-testid={`fp-card-${idx}`}
                            className="group bg-white rounded-3xl overflow-hidden flex flex-col card-lift border border-pm-black/5 hover:border-pm-yellow hover:shadow-[0_22px_50px_rgb(0,0,0,0.08)]"
                        >
                            <div className={`relative aspect-square overflow-hidden ${p.accent}`}>
                                <img
                                    src={p.image}
                                    alt={p.title}
                                    loading="lazy"
                                    className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                                />
                                <div className="absolute top-3 left-3 h-11 w-11 rounded-full bg-white shadow-md flex items-center justify-center">
                                    <p.icon className="w-5 h-5 text-pm-green" strokeWidth={1.8} />
                                </div>
                            </div>
                            <div className="p-5 md:p-6 flex-1 flex flex-col">
                                <h3 className="text-lg md:text-xl font-extrabold text-pm-black">{p.title}</h3>
                                <p className="mt-2 text-sm text-pm-black/60 leading-relaxed flex-1">{p.desc}</p>
                                <div className="mt-4 flex items-center gap-1.5 text-pm-green text-xs font-bold uppercase tracking-wider">
                                    <span>Learn more</span>
                                    <ArrowRight className="w-3.5 h-3.5" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* IN vs OUT comparison */}
        <section className="bg-white py-16 md:py-24 border-y border-pm-black/5">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center max-w-2xl mx-auto mb-12">
                    <span className="inline-block text-pm-orange font-bold tracking-[0.25em] uppercase text-[11px] mb-3">
                        What's In · What's Out
                    </span>
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-[-0.02em] text-pm-black leading-tight">
                        Radical <span className="text-pm-green">transparency.</span>
                    </h2>
                </div>

                <div className="grid md:grid-cols-2 gap-5 md:gap-6">
                    <div data-testid="fp-whats-in" className="rounded-3xl p-7 md:p-9 bg-pm-green text-white">
                        <span className="inline-flex items-center gap-2 text-pm-yellow font-bold tracking-[0.2em] uppercase text-xs mb-5">
                            <Check className="w-4 h-4" /> What goes in
                        </span>
                        <ul className="space-y-3.5">
                            {WHAT_IN.map((item) => (
                                <li key={item} className="flex items-start gap-3 text-base md:text-lg">
                                    <span className="flex-shrink-0 mt-1 h-5 w-5 rounded-full bg-pm-yellow text-pm-green flex items-center justify-center">
                                        <Check className="w-3 h-3" strokeWidth={3} />
                                    </span>
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div data-testid="fp-whats-out" className="rounded-3xl p-7 md:p-9 bg-pm-cream border border-pm-black/8">
                        <span className="inline-flex items-center gap-2 text-pm-orange font-bold tracking-[0.2em] uppercase text-xs mb-5">
                            <X className="w-4 h-4" /> What stays out
                        </span>
                        <ul className="space-y-3.5">
                            {WHAT_OUT.map((item) => (
                                <li key={item} className="flex items-start gap-3 text-base md:text-lg text-pm-black/80">
                                    <span className="flex-shrink-0 mt-1 h-5 w-5 rounded-full bg-pm-orange/15 text-pm-orange flex items-center justify-center">
                                        <X className="w-3 h-3" strokeWidth={3} />
                                    </span>
                                    <span className="line-through decoration-pm-black/20">{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </section>

        {/* THREE RULES */}
        <section className="bg-pm-cream py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-2xl mb-12">
                    <span className="inline-block text-pm-orange font-bold tracking-[0.25em] uppercase text-[11px] mb-3">
                        How We Build It
                    </span>
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-pm-black tracking-[-0.02em] leading-tight">
                        Three rules we <span className="text-pm-green">never</span> break.
                    </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                    {RULES.map((p, i) => (
                        <div
                            key={p.title}
                            data-testid={`fp-rule-${i}`}
                            className="bg-white p-6 md:p-7 rounded-2xl border border-pm-black/8 hover:border-pm-green transition-colors card-lift"
                        >
                            <div className="h-14 w-14 rounded-2xl bg-pm-green text-pm-yellow flex items-center justify-center mb-4">
                                <p.icon className="w-7 h-7" strokeWidth={1.8} />
                            </div>
                            <h3 className="text-lg md:text-xl font-extrabold text-pm-black">{p.title}</h3>
                            <p className="mt-2 text-sm text-pm-black/65 leading-relaxed">{p.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* FINAL CTA */}
        <section className="bg-pm-green text-white py-16 md:py-24 relative overflow-hidden">
            <div aria-hidden className="absolute -top-20 -left-20 w-80 h-80 rounded-full bg-pm-yellow/15 blur-3xl" />
            <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-pm-greenBright/30 blur-3xl" />
            <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-[-0.02em] leading-tight">
                    Cleaner ingredients. <br />
                    <span className="text-pm-yellow">Bolder flavors.</span>
                </h2>
                <p className="mt-4 text-white/85 max-w-xl mx-auto text-base md:text-lg">
                    Stop choosing between healthy and tasty. With ProMill, you get both — every meal, every time.
                </p>
                <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                    <Link to="/menu" data-testid="fp-cta-menu" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-pm-yellow text-pm-black font-bold hover:bg-pm-yellowBright transition-colors">
                        Explore Menu
                        <ArrowRight className="w-4 h-4" />
                    </Link>
                    <a href="https://wa.me/919517717917" target="_blank" rel="noreferrer" data-testid="fp-cta-enquire" className="hidden">
                        Enquire Now
                    </a>
                </div>
            </div>
        </section>

        <Newsletter />
        <Footer />
    </div>
);

export default FoodPromisePage;
